import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { ReactComponent as CallIcon } from 'assets/images/inbound-call.svg';
import { getTimeDifference } from 'utils/functions';
import { Call, CommunicationDisplay, CallResponse, CallTypes, Colors } from 'typings';

interface CallProps extends CommunicationDisplay {
  // TODO: update call data to return booleans rather than string '0' and '1'
  data: Call;
  children: React.ReactNode;
  caption?: React.ReactElement;
}

const useStyles = makeStyles((theme: Theme) => ({
  innerWrapper: {
    flexDirection: 'column',
    width: '100%',
  },
  text: {
    padding: theme.spacing(0, 1),
    borderBottom: `2px solid ${theme.palette.background.green}`,
  },
  caption: {
    fontWeight: 500,
    padding: theme.spacing(0, 1),
    color: theme.palette.grey['500'],
  },
  svg: {
    display: 'inline-flex',
    width: theme.spacing(4),
    borderRadius: '50%',
    boxShadow: `0 0 2px ${Colors.LIGHT_GRAY}`,
    '& circle': {
      fill: `${theme.palette.background.green}`,
    },
  },
}));

const CallContent = ({ data, children, caption }: React.PropsWithChildren<CallProps>) => {
  const classes = useStyles({ data, children });

  const {
    attemptedAgentTransfer,
    attemptedCallCenterTransfer,
    callResponse,
    callType,
    conciergeName,
    createdAt,
    endedAt,
    isAfterHours,
    successfulAgentTransfer,
    successfulCallCenterTransfer,
  } = data;

  let callMessage;
  switch (callType) {
    case CallTypes.INBOUND:
      callMessage = 'Inbound Call';
      if (attemptedCallCenterTransfer && !isAfterHours) {
        callMessage += ' Forwarded via CallConnect';
      } else if (attemptedCallCenterTransfer && isAfterHours) {
        callMessage += ' via CallConnect';
      } else if (callResponse === CallResponse.MISSED) {
        callMessage += ' Missed';
      } else {
        callMessage += ' Received';
      }
      break;

    case CallTypes.OUTBOUND:
      callMessage = attemptedCallCenterTransfer
        ? 'Outbound Call Forwarded via CallConnect'
        : 'Outbound Call Placed';
      break;

    case CallTypes.OUTBOUND_AGENT:
      // hide outbound agent calls that are part of call center transfers, deciding not to render each leg of the transfer
      if (attemptedCallCenterTransfer) return null;

      callMessage = 'Live Transfer';

      // for completed live transfer calls, add success indicator
      if (attemptedAgentTransfer && endedAt) {
        callMessage += successfulAgentTransfer ? ' (Successful)' : ' (Unsuccessful)';
      }
      break;

    default:
      return null;
  }

  // for completed call center calls, add success indicator
  if (attemptedCallCenterTransfer && endedAt && !isAfterHours) {
    callMessage += successfulCallCenterTransfer ? ' (Successful)' : ' (Unsuccessful)';
  } else if (attemptedCallCenterTransfer && endedAt && isAfterHours) {
    callMessage += ' (After Hours)';
  }

  const durationMessage = `- Lasted: ${getTimeDifference(createdAt, endedAt)}`;

  const answeredBy =
    callType === CallTypes.INBOUND && conciergeName && `(answered by ${conciergeName})`;

  return (
    <>
      {callType === 'inbound' && <CallIcon className={classes.svg} />}
      <Box className={classes.innerWrapper}>
        <Typography variant="body2" className={classes.text}>
          {callMessage} {durationMessage} {answeredBy}
        </Typography>
        {caption}
      </Box>
      {callType !== 'inbound' && <CallIcon className={classes.svg} />}
    </>
  );
};

export default CallContent;
